import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CustomInput from '@/components/CustomInput/CustomInput';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import loader from '../../assets/img/loader.svg';
import Button from '@/components/CustomButtons/Button';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import ManagedAccountsDropdown from '@/components/ManagedAccountsDropdown/ManagedAccountsDropdown';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import CardBody from '@/components/Card/CardBody';
import { API_BASE_URL, bankNames } from '@/config';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  IconButton,
  Box,
} from '@mui/material';
import { ContentCopy, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getOrganizations } from '@/views/Organizations/Organizations.api';
import { useGetStates } from '@/api/hooks';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export default function CreateOrUpdateSmartPayablesAccount() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');
  const [loaded, setLoaded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { states } = useGetStates('US');
  const [formInputs, setFormInputs] = useState({
    name: '',
    bank_alias: '',
    bank_account: '',
    routing_number: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    organization_id: organizationId,
    bank_name: '',
    managed_account_id: '',
    host: '',
    port: '',
    username: '',
    pp_folder: '',
    recon_folder: '',
    pp_filename: '',
    should_encrypt: false,
    is_fbo: false,
    password: '',
    bank_pgp_public_key: '',
    client_pgp_public_key: '',
    bank_ssh_public_key: '',
  });

  const [organizations, setOrganizations] = useState([]);
  const [signature, setSignature] = useState(null);
  const [signatureImg, setSignatureImg] = useState('');
  const [message, setMessage] = useState('');

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const buildPayload = (extra = {}) => ({
    ...formInputs,
    organization_id: parseInt(formInputs.organization_id),
    managed_account_id: formInputs.managed_account_id
      ? parseInt(formInputs.managed_account_id, 10)
      : null,
    ...extra,
  });

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormInputs((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onBankNameChange = (e) => {
    const { value } = e.target;

    setFormInputs((prev) => ({
      ...prev,
      bank_name: value,
      managed_account_id:
        value === bankNames.Wells_Fargo ? prev.managed_account_id : '',
    }));
  };

  const onManagedAccountChange = (e) => {
    setFormInputs((prev) => ({ ...prev, managed_account_id: e.target.value }));
  };

  const handleSignatureFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSignature(file);
      setSignatureImg(URL.createObjectURL(file));
    }
  };

  const removeSignature = () => {
    setSignature(null);
    setSignatureImg('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const createSmartPayablesAccount = async (e) => {
    try {
      e.preventDefault();

      setLoaded(true);
      setMessage('');

      if (signature) {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result;

          const payload = buildPayload({ sig_file: base64 });

          try {
            const response = isEditMode
              ? await axios.put(`${API_BASE_URL}/smartpayables/${id}`, payload)
              : await axios.post(API_BASE_URL + '/smartpayables', payload);

            if (response.status === 201 || response.status === 200) {
              setLoaded(false);
              navigate('/admin/checkManagement');
            }
          } catch (error) {
            console.error('Error saving SmartPayables account:', error);
            setMessage(
              error.response?.data?.message ||
                error.response?.data?.msg ||
                t('smartPayablesAccount.errors.saveAccount', {
                  action: isEditMode
                    ? t('smartPayablesAccount.actions.updating')
                    : t('smartPayablesAccount.actions.creating'),
                }),
            );
            setLoaded(false);
          }
        };
        reader.readAsDataURL(signature);
      } else {
        const payload = buildPayload();

        try {
          const response = isEditMode
            ? await axios.put(`${API_BASE_URL}/smartpayables/${id}`, payload)
            : await axios.post(API_BASE_URL + '/smartpayables', payload);

          if (response.status === 201 || response.status === 200) {
            setLoaded(false);
            navigate('/admin/checkManagement');
          }
        } catch (error) {
          console.error('Error saving SmartPayables account:', error);
          setMessage(
            error.response?.data?.message ||
              error.response?.data?.msg ||
              t('smartPayablesAccount.errors.saveAccount', {
                action: isEditMode
                  ? t('smartPayablesAccount.actions.updating')
                  : t('smartPayablesAccount.actions.creating'),
              }),
          );
          setLoaded(false);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage(t('smartPayablesAccount.errors.unexpected'));
      setLoaded(false);
    }
  };

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      const loadAccountData = async () => {
        try {
          setLoaded(true);
          const response = await axios.get(
            `${API_BASE_URL}/smartpayables/${id}`,
          );
          if (response.data) {
            setFormInputs({
              ...response.data,
              organization_id: response.data.organization_id || '',
              managed_account_id: response.data.managed_account_id ?? '',
            });
          }
        } catch (error) {
          console.error('Error loading SmartPayables account:', error);
          setMessage(t('smartPayablesAccount.errors.loadingData'));
        } finally {
          setLoaded(false);
        }
      };
      loadAccountData();
    }

    getOrganizations()
      .then((res) => {
        setOrganizations(res.data.organizations);
      })
      .catch((error) => {
        console.error('Error fetching organizations:', error);
      });
  }, [id]);

  const handleBackClick = () => {
    const url = organizationId
      ? `/admin/updateOrganization/${organizationId}#check-management`
      : '/admin/checkManagement';
    navigate(url);
  };

  return (
    <div>
      <form onSubmit={createSmartPayablesAccount}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color='primary'>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={handleBackClick}
                    sx={{
                      color: 'white !important',
                      padding: '4px',
                      '& .MuiSvgIcon-root': {
                        color: 'white !important',
                      },
                    }}
                    size='small'>
                    <ArrowBack sx={{ color: 'white !important' }} />
                  </IconButton>
                  <h4 style={{ margin: 0 }}>
                    {isEditMode
                      ? t('smartPayablesAccount.title.edit')
                      : t('smartPayablesAccount.title.create')}
                  </h4>
                </Box>
              </CardHeader>
              <CardBody>
                {loaded && (
                  <div className='loaders'>
                    <img src={loader} alt={t('common.loading')} />
                  </div>
                )}

                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl variant='standard' fullWidth>
                        <InputLabel id='organization-select-label'>
                          {t('smartPayablesAccount.form.organization')}
                        </InputLabel>
                        <Select
                          labelId='organization-select-label'
                          id='organization-select'
                          value={formInputs.organization_id}
                          onChange={onInputChange}
                          name='organization_id'
                          className='full-width-elem'
                          disabled={organizationId}>
                          <MenuItem value=''>
                            <em>
                              {t(
                                'smartPayablesAccount.form.selectOrganization',
                              )}
                            </em>
                          </MenuItem>
                          {organizations.map((org, index) => (
                            <MenuItem key={index} value={org.id}>
                              {org.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      type='text'
                      labelText={t('smartPayablesAccount.form.accountName')}
                      onChange={onInputChange}
                      value={formInputs.name}
                      name='name'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      type='text'
                      labelText={t('smartPayablesAccount.form.bankAlias')}
                      onChange={onInputChange}
                      value={formInputs.bank_alias}
                      name='bank_alias'
                      disabled={isEditMode}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t(
                        'smartPayablesAccount.form.bankAccountNumber',
                      )}
                      name='bank_account'
                      onChange={onInputChange}
                      value={formInputs.bank_account}
                      disabled={isEditMode}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.routingNumber')}
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.routingNumberTooltip',
                            )}
                            placement='top'
                          />
                        </Box>
                      }
                      name='routing_number'
                      onChange={onInputChange}
                      value={formInputs.routing_number}
                      disabled={isEditMode}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl variant='standard' fullWidth>
                        <InputLabel id='bank-name-label'>
                          {t('smartPayablesAccount.form.bankName')}
                        </InputLabel>
                        <Select
                          labelId='bank-name-label'
                          value={formInputs.bank_name}
                          onChange={onBankNameChange}
                          name='bank_name'
                          className='full-width-elem'>
                          <MenuItem value=''>
                            <em>{t('smartPayablesAccount.form.selectBank')}</em>
                          </MenuItem>
                          {Object.values(bankNames).map((bankName, index) => (
                            <MenuItem key={index} value={bankName}>
                              {bankName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </GridItem>

                  {formInputs.bank_name === bankNames.Wells_Fargo && (
                    <GridItem xs={12} sm={12} md={6}>
                      <div className='custom-select'>
                        <ManagedAccountsDropdown
                          value={formInputs.managed_account_id}
                          onChange={onManagedAccountChange}
                          label={t('smartPayablesAccount.form.managedAccount')}
                          placeholder={t(
                            'smartPayablesAccount.form.managedAccountNone',
                          )}
                          helperText={t(
                            'smartPayablesAccount.form.managedAccountHelp',
                          )}
                          variant='standard'
                          size='medium'
                        />
                      </div>
                    </GridItem>
                  )}

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('smartPayablesAccount.form.address')}
                      name='address'
                      onChange={onInputChange}
                      value={formInputs.address}
                      disabled={isEditMode}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={4}>
                    <CustomInput
                      labelText={t('smartPayablesAccount.form.city')}
                      name='city'
                      onChange={onInputChange}
                      value={formInputs.city}
                      disabled={isEditMode}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={4}>
                    <div className='custom-select'>
                      <FormControl
                        variant='standard'
                        fullWidth
                        disabled={isEditMode}>
                        <InputLabel id='state-label'>
                          {t('smartPayablesAccount.form.state')}
                        </InputLabel>
                        <Select
                          labelId='state-label'
                          value={formInputs.state || ''}
                          onChange={onInputChange}
                          name='state'
                          className='full-width-elem'>
                          <MenuItem value=''>
                            <em>
                              {t('smartPayablesAccount.form.selectState')}
                            </em>
                          </MenuItem>
                          {states.map((state) => (
                            <MenuItem
                              key={state.abbreviation}
                              value={state.abbreviation}>
                              {state.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </GridItem>

                  <GridItem xs={12} sm={12} md={4}>
                    <CustomInput
                      labelText={t('smartPayablesAccount.form.zipcode')}
                      name='zipcode'
                      onChange={onInputChange}
                      value={formInputs.zipcode}
                      disabled={isEditMode}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formInputs.is_fbo}
                            onChange={onInputChange}
                            name='is_fbo'
                          />
                        }
                        label={t('smartPayablesAccount.form.fboAccount')}
                      />
                      <InfoTooltip
                        title={t('smartPayablesAccount.form.fboAccountTooltip')}
                        placement='top'
                      />
                    </Box>
                  </GridItem>

                  {!isEditMode && (
                    <GridItem xs={12} sm={12} md={6}>
                      <div className='custom-upload m-b-30 m-t-27'>
                        <label htmlFor='signature'>
                          {t('smartPayablesAccount.form.uploadSignature')}
                        </label>
                        <br />
                        <label
                          htmlFor='signature'
                          className='custom-upload-btn'>
                          <span>{t('smartPayablesAccount.form.browse')}</span>
                          <input
                            ref={fileInputRef}
                            id='signature'
                            name='signature'
                            type='file'
                            accept='.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp'
                            style={{ display: 'none' }}
                            onChange={handleSignatureFile}
                          />
                        </label>
                      </div>

                      {signatureImg && (
                        <>
                          <div className='preview-image' width='130'>
                            <img
                              src={signatureImg}
                              width='130'
                              alt={t(
                                'smartPayablesAccount.form.signaturePreview',
                              )}
                            />
                          </div>
                          <span
                            className='material-icons'
                            style={{ cursor: 'pointer' }}
                            onClick={removeSignature}>
                            delete
                          </span>
                        </>
                      )}
                    </GridItem>
                  )}

                  <GridItem xs={12} sm={12} md={12}>
                    <h4 style={{ margin: '20px 0 10px 0', color: '#333' }}>
                      {t('smartPayablesAccount.sections.ftpConfiguration')}
                    </h4>
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.host')}
                          <InfoTooltip
                            title={t('smartPayablesAccount.form.hostTooltip')}
                            placement='top'
                          />
                        </Box>
                      }
                      name='host'
                      onChange={onInputChange}
                      value={formInputs.host}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.port')}
                          <InfoTooltip
                            title={t('smartPayablesAccount.form.portTooltip')}
                            placement='top'
                          />
                        </Box>
                      }
                      name='port'
                      type='number'
                      onChange={onInputChange}
                      value={formInputs.port}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.username')}
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.usernameTooltip',
                            )}
                            placement='top'
                          />
                        </Box>
                      }
                      name='username'
                      onChange={onInputChange}
                      value={formInputs.username}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.password')}
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.passwordTooltip',
                            )}
                            placement='top'
                          />
                        </Box>
                      }
                      name='password'
                      type='password'
                      onChange={onInputChange}
                      value={formInputs.password}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={12}>
                    <h4 style={{ margin: '20px 0 10px 0', color: '#333' }}>
                      {t('smartPayablesAccount.sections.folderConfiguration')}
                    </h4>
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.positivePayFolder')}
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.positivePayFolderTooltip',
                            )}
                            placement='top'
                          />
                        </Box>
                      }
                      name='pp_folder'
                      onChange={onInputChange}
                      value={formInputs.pp_folder}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.reconFolder')}
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.reconFolderTooltip',
                            )}
                            placement='top'
                          />
                        </Box>
                      }
                      name='recon_folder'
                      onChange={onInputChange}
                      value={formInputs.recon_folder}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={12}>
                    <h4 style={{ margin: '20px 0 10px 0', color: '#333' }}>
                      {t('smartPayablesAccount.sections.filenameConfiguration')}
                    </h4>
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('smartPayablesAccount.form.positivePayFilename')}
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.positivePayFilenameTooltip',
                            )}
                            placement='top'
                          />
                        </Box>
                      }
                      name='pp_filename'
                      onChange={onInputChange}
                      value={formInputs.pp_filename}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formInputs.should_encrypt}
                          onChange={onInputChange}
                          name='should_encrypt'
                          color='primary'
                        />
                      }
                      label={t(
                        'smartPayablesAccount.form.enableFileEncryption',
                      )}
                      style={{ marginTop: '20px' }}
                    />
                  </GridItem>

                  <GridItem xs={12} sm={12} md={12}>
                    <h4 style={{ margin: '20px 0 10px 0', color: '#333' }}>
                      {t('smartPayablesAccount.sections.bankPgpPublicKey')}
                    </h4>
                  </GridItem>

                  <GridItem xs={12} sm={12} md={12}>
                    <div style={{ marginBottom: '20px' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '8px',
                        }}>
                        <label
                          htmlFor='bank_pgp_public_key'
                          style={{
                            display: 'block',
                            fontWeight: '400',
                            color: '#333',
                          }}>
                          {t('smartPayablesAccount.form.bankPgpPublicKey')}
                        </label>
                        <InfoTooltip
                          title={t(
                            'smartPayablesAccount.form.bankPgpKeyTooltip',
                          )}
                          placement='top'
                        />
                      </Box>
                      <textarea
                        id='bank_pgp_public_key'
                        name='bank_pgp_public_key'
                        value={formInputs.bank_pgp_public_key}
                        onChange={onInputChange}
                        placeholder={t(
                          'smartPayablesAccount.form.bankPgpKeyPlaceholder',
                        )}
                        style={{
                          width: '100%',
                          minHeight: '120px',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </GridItem>

                  {isEditMode && (
                    <>
                      <GridItem xs={12} sm={12} md={12}>
                        <h4 style={{ margin: '20px 0 10px 0', color: '#333' }}>
                          {t(
                            'smartPayablesAccount.sections.clientPgpPublicKey',
                          )}
                        </h4>
                      </GridItem>

                      <GridItem xs={12} sm={12} md={12}>
                        <div style={{ marginBottom: '20px' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '8px',
                            }}>
                            <label
                              htmlFor='client_pgp_public_key'
                              style={{
                                display: 'block',
                                fontWeight: '400',
                                color: '#333',
                                marginRight: '10px',
                              }}>
                              {t(
                                'smartPayablesAccount.form.clientPgpPublicKey',
                              )}
                            </label>
                            <InfoTooltip
                              title={t(
                                'smartPayablesAccount.form.clientPgpKeyTooltip',
                              )}
                              placement='top'
                            />
                            {formInputs.client_pgp_public_key && (
                              <IconButton
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    formInputs.client_pgp_public_key,
                                  );
                                  toast.success(
                                    t(
                                      'smartPayablesAccount.toast.clientPgpKeyCopied',
                                    ),
                                  );
                                }}
                                color='primary'
                                size='small'
                                title='Copy to clipboard'
                                style={{ marginLeft: '5px' }}>
                                <ContentCopy fontSize='small' />
                              </IconButton>
                            )}
                          </div>
                          <textarea
                            id='client_pgp_public_key'
                            name='client_pgp_public_key'
                            value={formInputs.client_pgp_public_key}
                            onChange={onInputChange}
                            disabled={true}
                            placeholder={t(
                              'smartPayablesAccount.form.clientPgpKeyPlaceholder',
                            )}
                            style={{
                              width: '100%',
                              minHeight: '120px',
                              padding: '12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontFamily: 'monospace',
                              fontSize: '14px',
                              resize: 'vertical',
                              boxSizing: 'border-box',
                              backgroundColor: '#f5f5f5',
                              cursor: 'not-allowed',
                            }}
                          />
                        </div>
                      </GridItem>
                    </>
                  )}
                </GridContainer>

                {message && (
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <p style={{ color: 'red', padding: '10px 0' }}>
                        {message}
                      </p>
                    </GridItem>
                  </GridContainer>
                )}

                {isEditMode && (
                  <>
                    <GridItem xs={12} sm={12} md={12}>
                      <h4 style={{ margin: '20px 0 10px 0', color: '#333' }}>
                        {t('smartPayablesAccount.sections.bankSshPublicKey')}
                      </h4>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={12}>
                      <div style={{ marginBottom: '20px' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}>
                          <label
                            htmlFor='bank_ssh_public_key'
                            style={{
                              display: 'block',
                              fontWeight: '400',
                              color: '#333',
                              marginRight: '10px',
                            }}>
                            {t('smartPayablesAccount.form.bankSshPublicKey')}
                          </label>
                          <InfoTooltip
                            title={t(
                              'smartPayablesAccount.form.bankSshKeyTooltip',
                            )}
                            placement='top'
                          />
                          {formInputs.bank_ssh_public_key && (
                            <IconButton
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  formInputs.bank_ssh_public_key,
                                );
                                toast.success(
                                  t(
                                    'smartPayablesAccount.toast.bankSshKeyCopied',
                                  ),
                                );
                              }}
                              color='primary'
                              size='small'
                              title='Copy to clipboard'
                              style={{ marginLeft: '5px' }}>
                              <ContentCopy fontSize='small' />
                            </IconButton>
                          )}
                        </div>
                        <textarea
                          id='bank_ssh_public_key'
                          name='bank_ssh_public_key'
                          value={formInputs.bank_ssh_public_key || ''}
                          disabled={true}
                          placeholder={t(
                            'smartPayablesAccount.form.bankSshKeyPlaceholder',
                          )}
                          style={{
                            width: '100%',
                            minHeight: '120px',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            backgroundColor: '#f5f5f5',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>
                    </GridItem>
                  </>
                )}

                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <Button color='primary' type='submit' disabled={loaded}>
                      {loaded
                        ? t('smartPayablesAccount.buttons.saving')
                        : isEditMode
                        ? t('smartPayablesAccount.buttons.updateAccount')
                        : t('smartPayablesAccount.buttons.createAccount')}
                    </Button>
                    <Button
                      color='primary'
                      onClick={handleBackClick}
                      className='btn-cancel'
                      style={{ marginLeft: '5px' }}>
                      {t('smartPayablesAccount.buttons.cancel')}
                    </Button>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </form>
    </div>
  );
}
